/**
 * Completivos del inicial x Mayor.
 *
 * Facturas x mayor a crédito cuyo inicial pactado (cuota 0 del plan, ver
 * ventasxmayorbackend.CUOTA_INICIAL) no se ha cobrado completo: pactado,
 * cobrado, pendiente, fecha de vencimiento del completivo y días de atraso.
 * Filtros: vencidos / próximos a vencer en N días / todos, sucursal y
 * cliente. Cada fila va en la moneda de su factura y los totales se dan por
 * moneda (no se suman pesos con dólares). Desde la fila se abre el detalle
 * de la factura, que es donde se cobra.
 *
 * Mismo permiso que la antigüedad de saldo (ares9725).
 */
import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi } from "lib/api";
import { monedaLabel } from "lib/moneda";
import { imprimirListadoGrande } from "lib/impresionhtml";
import { fmtMoney } from "modules/ventasxmayor/impresionxmayor";
import FacturaDetalleXmayor from "modules/ventasxmayor/facturadetallexmayor";

const MODOS = [
  { value: "vencidos", label: "Vencidos" },
  { value: "proximos", label: "Próximos a vencer" },
  { value: "todos", label: "Todos los pendientes" },
];

const btnBase = { textTransform: "none", borderRadius: 1.8, fontWeight: 700, boxShadow: "none" };
const btnSolid = (bg, hover) => ({
  ...btnBase,
  bgcolor: bg,
  color: "#FFFFFF",
  "&:hover": { bgcolor: hover, boxShadow: "none", color: "#FFFFFF" },
  "&.Mui-disabled": { bgcolor: `${bg}66`, color: "#FFFFFF" },
});
const btnOutline = (c) => ({
  ...btnBase,
  color: c,
  bgcolor: "#FFFFFF",
  border: `1px solid ${c}`,
  "&:hover": { bgcolor: `${c}12`, border: `1px solid ${c}`, color: c },
});

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 0,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

function NoRowsOverlay() {
  return (
    <Stack
      alignItems="center"
      justifyContent="center"
      sx={{ height: "100%", py: 6, color: FP.color.textMuted }}
    >
      <InboxOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
      <Typography sx={{ fontWeight: 700, color: FP.color.textSecondary }}>
        Sin completivos — procese el reporte
      </Typography>
    </Stack>
  );
}

function ResumenChip({ label, lineas, color = FP.color.navy }) {
  return (
    <Box
      sx={{
        px: 1.8,
        py: 0.8,
        borderRadius: 1.8,
        border: `1px solid ${FP.color.border}`,
        bgcolor: "#fff",
        minWidth: 140,
      }}
    >
      <Typography sx={{ fontSize: "0.68rem", fontWeight: 800, color: FP.color.textMuted }}>
        {label}
      </Typography>
      {(lineas.length ? lineas : ["0.00"]).map((l) => (
        <Typography key={l} sx={{ fontSize: "0.95rem", fontWeight: 800, color }}>
          {l}
        </Typography>
      ))}
    </Box>
  );
}

const moneyCol = (field, headerName, width = 120) => ({
  field,
  headerName,
  width,
  align: "right",
  headerAlign: "right",
  valueFormatter: (value) => fmtMoney(value),
});

export default function CompletivosXmayor() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();

  const [modo, setModo] = useState("vencidos");
  const [dias, setDias] = useState("7");
  const [sucursal, setSucursal] = useState("");
  const [clientes, setClientes] = useState([]);
  const [cliente, setCliente] = useState(null);

  const [filas, setFilas] = useState([]);
  const [totales, setTotales] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [procesado, setProcesado] = useState(null);
  const [habilitado, setHabilitado] = useState(true);
  const [loading, setLoading] = useState(false);
  const [detalleId, setDetalleId] = useState(null);

  const procesar = async () => {
    setLoading(true);
    try {
      const { ok, data } = await post("/api/xmayor_completivos", {
        modo,
        dias: Number(dias) || 7,
        sucursal,
        cliente: cliente?.id || "",
      });
      if (ok) {
        setFilas(data.completivos || []);
        setTotales(data.totales || []);
        setSucursales(data.sucursales || []);
        setHabilitado(data.habilitado !== false);
        setProcesado({ modo, dias: Number(dias) || 7, hoy: data.hoy });
      } else {
        showAlert({ message: data.Error || "No se pudo generar el reporte", color: "error" });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    post("/api/xmayor_clientes", {}).then(({ ok, data }) => {
      if (ok) setClientes(data.clientes || []);
    });
    procesar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hayDatos = filas.length > 0;
  const lineasMoneda = (campo) =>
    totales.map((t) => `${monedaLabel(t.moneda)} ${fmtMoney(t[campo])}`);

  const subtitulo = (() => {
    if (!procesado) return "";
    const partes = [
      procesado.modo === "proximos"
        ? `Próximos a vencer en ${procesado.dias} día(s)`
        : MODOS.find((m) => m.value === procesado.modo)?.label,
      `Al ${procesado.hoy}`,
    ];
    const suc = sucursales.find((s) => s.sucursal === sucursal);
    if (sucursal) partes.push(`Sucursal ${sucursal}${suc ? ` — ${suc.descripcion}` : ""}`);
    if (cliente) partes.push(`Cliente ${cliente.descrip}`);
    return partes.join(" · ");
  })();

  const cabeceras = [
    "Fact#",
    "Fecha",
    "Cliente",
    "Suc.",
    "Moneda",
    "Pactado",
    "Cobrado",
    "Pendiente",
    "Vence",
    "Atraso",
  ];
  const filaListado = (f) => [
    f.id,
    f.fecha,
    f.cliente,
    f.sucursal,
    monedaLabel(f.moneda),
    fmtMoney(f.pactado),
    fmtMoney(f.cobrado),
    fmtMoney(f.pendiente),
    f.vence,
    f.atraso > 0 ? `${f.atraso} días` : "",
  ];
  const filasTotales = totales.map((t) => [
    "",
    "",
    `TOTAL ${monedaLabel(t.moneda)} (${t.facturas} factura(s))`,
    "",
    "",
    fmtMoney(t.pactado),
    fmtMoney(t.cobrado),
    fmtMoney(t.pendiente),
    "Vencido:",
    fmtMoney(t.vencido),
  ]);

  const exportar = () => {
    const hoja = XLSX.utils.aoa_to_sheet([
      ["Completivos del inicial x Mayor"],
      [subtitulo],
      [],
      cabeceras,
      ...filas.map((f) => [
        f.id,
        f.fecha,
        f.cliente,
        f.sucursal,
        monedaLabel(f.moneda),
        f.pactado,
        f.cobrado,
        f.pendiente,
        f.vence,
        f.atraso,
      ]),
      [],
      ...totales.map((t) => [
        "",
        "",
        `TOTAL ${monedaLabel(t.moneda)}`,
        "",
        "",
        t.pactado,
        t.cobrado,
        t.pendiente,
        "Vencido",
        t.vencido,
      ]),
    ]);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, "Completivos");
    XLSX.writeFile(libro, `CompletivosXmayor_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const imprimir = () => {
    imprimirListadoGrande({
      titulo: "Completivos del inicial x Mayor",
      subtitulo,
      cabeceras,
      filas: filas.map(filaListado),
      totales: filasTotales[0],
      totalesextra: filasTotales.slice(1),
      numericas: [5, 6, 7, 9],
    });
  };

  const columnas = [
    { field: "id", headerName: "Fact#", width: 80 },
    { field: "fecha", headerName: "Fecha", width: 105 },
    { field: "cliente", headerName: "Cliente", flex: 1, minWidth: 190 },
    {
      field: "sucursal",
      headerName: "Suc.",
      width: 70,
      renderCell: (p) => <span title={p.row.sucursalnombre}>{p.value}</span>,
    },
    {
      field: "moneda",
      headerName: "Moneda",
      width: 80,
      valueFormatter: (value) => monedaLabel(value),
    },
    moneyCol("pactado", "Pactado"),
    moneyCol("cobrado", "Cobrado"),
    moneyCol("pendiente", "Pendiente"),
    { field: "vence", headerName: "Vence", width: 105 },
    {
      field: "atraso",
      headerName: "Estado",
      width: 140,
      sortable: true,
      renderCell: (p) =>
        p.row.vencido ? (
          <Chip
            size="small"
            label={`Vencido ${p.row.atraso} día(s)`}
            sx={{ bgcolor: "#FDECEA", color: FP.color.danger, fontWeight: 700 }}
          />
        ) : (
          <Chip
            size="small"
            label="Por vencer"
            sx={{ bgcolor: "#FFF3E0", color: "#E65100", fontWeight: 700 }}
          />
        ),
    },
    {
      field: "abrir",
      headerName: "",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (p) => (
        <Button size="small" onClick={() => setDetalleId(p.row.id)} sx={btnOutline(FP.color.navy)}>
          Ver / cobrar
        </Button>
      ),
    },
  ];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Completivos del inicial x Mayor"
        subtitle="Facturas a crédito con inicial pactado pendiente de cobro, por fecha de vencimiento"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Grid container spacing={2} sx={{ mb: 1.5 }} alignItems="center">
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Mostrar"
                value={modo}
                onChange={(e) => setModo(e.target.value)}
                sx={fpInputSx}
              >
                {MODOS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {modo === "proximos" && (
              <Grid item xs={6} sm={2} md={1.5}>
                <TextField
                  fullWidth
                  type="number"
                  label="Días"
                  value={dias}
                  onChange={(e) => setDias(e.target.value)}
                  inputProps={{ min: 1, max: 365 }}
                  sx={fpInputSx}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={sucursal}
                onChange={(e) => setSucursal(e.target.value)}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map((s) => (
                  <MenuItem key={s.sucursal} value={s.sucursal}>
                    {s.sucursal} — {s.descripcion}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <Autocomplete
                options={clientes}
                value={cliente}
                onChange={(_, v) => setCliente(v)}
                getOptionLabel={(o) => o?.descrip || ""}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={(params) => (
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  <TextField {...params} label="Cliente (opcional)" sx={fpInputSx} />
                )}
              />
            </Grid>
            <Grid item xs={6} sm={2} md={2}>
              <Button
                startIcon={
                  loading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon />
                }
                onClick={procesar}
                disabled={loading}
                sx={btnSolid(FP.color.navy, FP.color.navySoft)}
              >
                Procesar
              </Button>
            </Grid>
          </Grid>

          {!habilitado && (
            <Typography
              sx={{ mb: 1.6, fontWeight: 700, fontSize: "0.82rem", color: FP.color.textSecondary }}
            >
              El inicial en ventas x mayor está deshabilitado en Configuración: no se pueden pactar
              iniciales nuevos. Los ya pactados se siguen listando y cobrando aquí.
            </Typography>
          )}
          <Stack direction="row" spacing={1.2} flexWrap="wrap" sx={{ mb: 1.6 }} useFlexGap>
            <ResumenChip label="PENDIENTE" lineas={lineasMoneda("pendiente")} />
            <ResumenChip label="VENCIDO" lineas={lineasMoneda("vencido")} color="#C62828" />
            <ResumenChip label="COBRADO" lineas={lineasMoneda("cobrado")} color="#2E7D32" />
            <ResumenChip
              label="FACTURAS"
              lineas={hayDatos ? [String(filas.length)] : []}
              color={FP.color.textSecondary}
            />
            <Box sx={{ flexGrow: 1 }} />
            <Button
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={exportar}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.success)}
            >
              Excel
            </Button>
            <Button
              startIcon={<PrintOutlinedIcon />}
              onClick={imprimir}
              disabled={!hayDatos}
              sx={btnOutline(FP.color.navy)}
            >
              Imprimir
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={filas}
              columns={columnas}
              getRowId={(r) => r.id}
              loading={loading}
              rowHeight={46}
              pageSizeOptions={[25, 50, 100]}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              slots={{ noRowsOverlay: NoRowsOverlay }}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "220px" }}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      </Box>

      {/* Detalle de la factura: ahí se ve el inicial y se cobra */}
      <Dialog fullScreen open={!!detalleId} onClose={() => setDetalleId(null)}>
        {detalleId && (
          <FacturaDetalleXmayor
            facturaId={detalleId}
            onExit={() => {
              setDetalleId(null);
              procesar();
            }}
            onChanged={procesar}
          />
        )}
      </Dialog>
    </Box>
  );
}
